import React, { useState } from 'react';
import css from './ShareLinks.scss';
import { pressedEnterOrSpace } from 'utils/accessibility';
import Icon from 'SF/MaterialIcon';
import { getConnectionURL, getScheduleConnectionURL } from 'utils/deepLinks';
import copy from 'clipboard-copy';
import { SFEntityID } from 'utils/types';
import PropTypes from 'prop-types';
import Switch from 'common/Switch';
import { getWord } from 'utils/tenant';
import Toast from 'common/Toast';

const ShareLinks = ({ appointmentLinkAvailable, profileLinkAvailable, setValue, userId }) => {
    const [ showInfo, setShowInfo ] = useState(false);
    const [ showToast, setShowToast ] = useState(false);

    const copyAppointmentLink = async () => {
        try {
            await copy(getScheduleConnectionURL(userId));
            setShowToast(true);
        } catch (e) {
            console.warn(e);
        }
    };

    const copyProfileLink = async () => {
        try {
            await copy(getConnectionURL(userId));
            setShowToast(true);
        } catch (e) {
            console.warn(e);
        }
    };
    
    const getShareLinksInfo = () => (
        <div className={css['info-container']} data-testid="links-info">
            <div className={css['info']}>
                <b>For students:</b> Copy the URL(s) to the clipboard. You can paste the URL(s) in emails, 
                email signatures, a non-{getWord('Starfish')} web page, etc.
                <span className={css['sub-info']}>
                    Students who receive a URL will only be able to use it if they have a relationship in {getWord('Starfish')} with you.
                    Note: If you are having trouble copying a URL, try using another web browser.
                </span>
                <b>To share to staff:</b> Select the appropriate checkbox(es) below to display the URL(s) on your 
                Connection Profile in the Services tab.
            </div>
        </div>
    );
    
   return (
       <div className={css['share-links']}>
           <h3>Share Links</h3>
           <p>Share your appointment and/or profile link with students and other staff members.</p>
           <a className={css['info-link']}
              href="#"
              onClick={() => setShowInfo(!showInfo)}
              onKeyDown={(e) => { if (pressedEnterOrSpace(e)) {
                  e.preventDefault();
                  setShowInfo(!showInfo);
              }}}>
               <Icon icon="help" aria-hidden={true} />{showInfo ? " Hide 'Show Me How'" : " Show Me How"}
           </a>
           { showInfo && getShareLinksInfo() }
           <div className={css['link-container']} aria-labelledby="appt-link">
               <label id="appt-link">Link to schedule an appointment with me</label>
               <span>{getScheduleConnectionURL(userId)}</span>
               <a data-testid="copy-appt-link"
                  href="#"
                  onClick={copyAppointmentLink}
                  onKeyDown={(e) => { if (pressedEnterOrSpace(e)) {
                      e.preventDefault();
                      // noinspection JSIgnoredPromiseFromCall
                      copyAppointmentLink();
                  }}}>
                   Copy URL to clipboard
               </a>
               <div className={css['availability-switch']}>
                   <Switch aria-labelledby="appt-switch" 
                           checked={appointmentLinkAvailable} 
                           onChange={(checked) => setValue({ appointmentLinkAvailable: checked })} />
                   <label id="appt-switch">Make URL available on my profile in the Service Catalog for other staff.</label>
               </div>
           </div>
           <div className={css['link-container']} aria-labelledby="profile-link">
               <label id="profile-link">Link to view my profile</label>
               <span>{getConnectionURL(userId)}</span>
               <a data-testid="copy-profile-link"
                  href="#"
                  onClick={copyProfileLink}
                  onKeyDown={(e) => { if (pressedEnterOrSpace(e)) {
                      e.preventDefault();
                      // noinspection JSIgnoredPromiseFromCall
                      copyProfileLink();
                  }}}>
                   Copy URL to clipboard
               </a>
               <div className={css['availability-switch']}>
                   <Switch aria-labelledby="profile-switch"
                           checked={profileLinkAvailable}
                           onChange={(checked) => setValue({ profileLinkAvailable: checked })} />
                   <label id="profile-switch">Make URL available on my profile in the Service Catalog for other staff.</label>
               </div>
           </div>
           { showToast && <Toast message={"Link copied to your clipboard."} onTimeout={() => setShowToast(false)} /> }
       </div>
   );
};

ShareLinks.propTypes = {
    appointmentLinkAvailable: PropTypes.bool,
    profileLinkAvailable: PropTypes.bool,
    setValue: PropTypes.func.isRequired,
    userId: SFEntityID.isRequired
};

export default ShareLinks;
